import { Box } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import StarOutlineIcon from '@mui/icons-material/StarOutline';

interface StarRatingProps {
  /**
   * Rating value (1-5, supports decimals)
   */
  rating: number;
  /**
   * Size of stars in pixels
   * @default 20
   */
  size?: number;
  /**
   * Color of filled stars
   * @default '#FFA500' (orange)
   */
  color?: string;
  /**
   * Whether to show the rating number
   * @default false
   */
  showValue?: boolean;
}

/**
 * StarRating - Display-only star rating component
 *
 * Displays a star rating with support for half stars and custom sizing.
 * For interactive star input, use StarRatingInput component.
 *
 * @example
 * <StarRating rating={4.5} size={24} showValue />
 * // Displays: ★★★★½ 4.5
 */
export function StarRating({
  rating,
  size = 20,
  color = '#FFA500',
  showValue = false
}: StarRatingProps) {
  // Clamp rating between 0 and 5
  const clampedRating = Math.max(0, Math.min(5, rating));

  const stars = [];

  for (let i = 1; i <= 5; i++) {
    const diff = clampedRating - i;

    if (diff >= 0) {
      // Full star
      stars.push(
        <StarIcon
          key={i}
          sx={{
            fontSize: size,
            color,
            marginRight: '2px'
          }}
        />
      );
    } else if (diff > -1 && diff < 0) {
      // Half star
      stars.push(
        <StarHalfIcon
          key={i}
          sx={{
            fontSize: size,
            color,
            marginRight: '2px'
          }}
        />
      );
    } else {
      // Empty star
      stars.push(
        <StarOutlineIcon
          key={i}
          sx={{
            fontSize: size,
            color: '#E0E0E0',
            marginRight: '2px'
          }}
        />
      );
    }
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: showValue ? 1 : 0
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {stars}
      </Box>
      {showValue && (
        <Box
          component="span"
          sx={{
            fontSize: size * 0.8,
            fontWeight: 600,
            color: 'text.secondary'
          }}
        >
          {clampedRating.toFixed(1)}
        </Box>
      )}
    </Box>
  );
}
